import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AppleLogo, Check, Copy, GooglePlayLogo } from '@phosphor-icons/react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { APP_STORE_LINKS } from '@/lib/appStoreLinks';

export type AppStore = 'ios' | 'android';

interface AppStoreQrDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which store's install code to show. */
  store: AppStore;
}

const STORES: Record<AppStore, { title: string; storeName: string; href: string; icon: React.ReactNode }> = {
  ios: { title: 'Ruta Crew on iPhone', storeName: 'App Store', href: APP_STORE_LINKS.ios, icon: <AppleLogo className="h-5 w-5" /> },
  android: { title: 'Ruta Crew on Android', storeName: 'Google Play', href: APP_STORE_LINKS.android, icon: <GooglePlayLogo className="h-5 w-5" /> },
};

/**
 * The crew-app install code, big enough to scan from a phone held up to the screen.
 * Opened from the QR button on the share menu's iOS / Android rows.
 */
export const AppStoreQrDialog = React.memo<AppStoreQrDialogProps>(({ open, onOpenChange, store }) => {
  const { title, storeName, href, icon } = STORES[store];
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the link is on screen to copy by hand.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[380px] p-0 gap-0 overflow-hidden">
        <DialogHeader variant="bordered">
          <DialogTitle className="flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <p className="text-sm text-muted-foreground">
            Point the phone's camera at the code to open the {storeName} listing.
          </p>
          <div className="flex justify-center rounded-lg border border-stone-200 bg-white p-4" data-testid={`qr-${store}`}>
            <QRCodeSVG value={href} size={220} bgColor="#ffffff" fgColor="#1c1917" />
          </div>
          <p className="break-all text-center text-xs text-muted-foreground">{href}</p>
        </DialogBody>

        <DialogFooter variant="bordered">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={copyLink}>
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" weight="bold" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy link
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
AppStoreQrDialog.displayName = 'AppStoreQrDialog';
